import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Search, Users, Rocket, BarChart3, ArrowRight, Shield } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { getMsalInstance, microsoftLoginRequest } from '../../config/oauth';
import { post } from '../../lib/apiClient';
import { API_URL } from '../../_config';
import { generateIdempotencyKey } from '../../lib/idempotency';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

const stats = [
  { icon: Users, value: '210M+', label: 'Verified Contacts' },
  { icon: Search, value: '35M+', label: 'Companies Tracked' },
  { icon: Rocket, value: '2,000+', label: 'Sales Teams' },
  { icon: BarChart3, value: '98%', label: 'Data Accuracy' },
];

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'microsoft' | null>(null);
  const [otpStep, setOtpStep] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const { loginWithApiResponse } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('payment') === 'success';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    if (!password) { setError('Please enter your password'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await post<{
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        expires_in?: number;
        user?: any;
        requires_otp?: boolean;
        email?: string;
      }>(
        API_URL.AUTH.LOGIN,
        { email, password },
        { headers: { 'Idempotency-Key': generateIdempotencyKey() } },
      );
      if (res.data.requires_otp) {
        setPendingEmail(res.data.email ?? email);
        setOtpStep(true);
        setOtpCode('');
        setError('');
      } else {
        loginWithApiResponse(res.data as any);
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      setError(err.message ?? 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await post<{ access_token: string; refresh_token: string; token_type: string; expires_in: number; user: any }>(
        'auth/verify-otp',
        { email: pendingEmail, otp_code: otpCode },
        { headers: { 'Idempotency-Key': generateIdempotencyKey() } },
      );
      loginWithApiResponse(res.data as any);
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.message ?? 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
        setError('Google login is not configured yet. Add VITE_GOOGLE_CLIENT_ID to .env.local');
        return;
      }
      setSocialLoading('google');
      setError('');
      try {
        const res = await fetch(`${API_BASE}auth/social/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenResponse.access_token }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.error?.message ?? 'Google login failed. Please try again.');
          return;
        }
        loginWithApiResponse(data.data);
        navigate('/app/dashboard');
      } catch {
        setError('Google login failed. Please try again.');
      } finally {
        setSocialLoading(null);
      }
    },
    onError: () => {
      setError('Google login was cancelled or failed.');
      setSocialLoading(null);
    },
  });

  const handleMicrosoftLogin = async () => {
    if (!import.meta.env.VITE_MICROSOFT_CLIENT_ID) {
      setError('Microsoft login is not configured yet. Add VITE_MICROSOFT_CLIENT_ID to .env.local');
      return;
    }
    setSocialLoading('microsoft');
    setError('');
    try {
      const msalInstance = await getMsalInstance();
      // Clear any stale MSAL interaction lock from a previous failed/closed popup
      Object.keys(sessionStorage)
        .filter(k => k.includes("interaction.status"))
        .forEach(k => sessionStorage.removeItem(k));
      const tokenResponse = await msalInstance.loginPopup(microsoftLoginRequest);
      const res = await fetch(`${API_BASE}auth/social/microsoft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenResponse.accessToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error?.message ?? 'Microsoft login failed. Please try again.');
        return;
      }
      loginWithApiResponse(data.data);
      navigate('/app/dashboard');
    } catch (err: any) {
      if (err?.errorCode !== 'user_cancelled') {
        setError('Microsoft login failed. Please try again.');
      }
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="au-wrap">
      <div className="au-left">
        <div className="au-left-dots" />
        <div className="au-left-glow" />
        <div className="au-left-inner">
          <Link to="/" className="au-logo-link">
            <img src="/images/logo.png" alt="LeadNova" className="au-logo" />
          </Link>
          <h1 className="au-left-title">Find, engage &<br />convert your ideal buyers</h1>
          <p className="au-left-desc">
            LeadNova's AI-powered platform helps sales teams discover high-quality leads and close more deals, faster.
          </p>
          <div className="au-stats-grid">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="au-stat">
                  <div className="au-stat-icon"><Icon size={20} color="#4DB8E8" /></div>
                  <div>
                    <div className="au-stat-value">{s.value}</div>
                    <div className="au-stat-label">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="au-testimonial">
            <p className="au-testimonial-quote">
              "LeadNova transformed our outbound process. We went from 50 leads a week to over 500 qualified prospects in the first month."
            </p>
            <div className="au-testimonial-author">
              <div className="au-testimonial-avatar">JR</div>
              <div>
                <div className="au-testimonial-name">Jessica Rivera</div>
                <div className="au-testimonial-role">VP Sales, TechFlow Inc</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="au-right">
        {socialLoading !== null && (
          <div className="au-social-overlay">
            <div className="au-social-overlay-spinner" />
            <p className="au-social-overlay-title">
              Signing you in with {socialLoading === 'google' ? 'Google' : 'Microsoft'}…
            </p>
            <p className="au-social-overlay-sub">Please wait a moment</p>
          </div>
        )}
        <div className="au-right-inner">
          <div className="au-mobile-logo">
            <Link to="/"><img src="/images/logo.png" alt="LeadNova" className="au-logo-sm" /></Link>
          </div>
          <div className="au-form-header">
            <h2 className="au-form-title">Welcome back</h2>
            <p className="au-form-subtitle">Log in to your LeadNova account</p>
          </div>

          {otpStep ? (
            /* OTP verification screen */
            <form onSubmit={handleOtpSubmit}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--sky-light)', color: 'var(--sky)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <Shield size={24} />
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>Check your email</h2>
                <p style={{ fontSize: 14, color: 'var(--gray-500)' }}>
                  We sent a 6-digit code to <strong>{pendingEmail}</strong>
                </p>
              </div>

              {error && <div className="au-error">{error}</div>}
              {resendSent && <div className="au-success">Go back and log in again to receive a new code.</div>}

              <div className="au-field" style={{ marginBottom: 24 }}>
                <label className="au-label">Verification Code</label>
                <input
                  className="au-input"
                  placeholder="000000"
                  maxLength={6}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ letterSpacing: 10, fontSize: 24, textAlign: 'center', height: 56 }}
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button type="submit" className="au-submit-btn" disabled={loading || otpCode.length !== 6}>
                {loading ? 'Verifying…' : 'Verify Code'}
              </button>

              <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--gray-500)' }}>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: 'var(--sky)', cursor: 'pointer', fontSize: 14 }}
                  onClick={() => { setOtpStep(false); setOtpCode(''); setError(''); setResendSent(false); }}
                >
                  ← Back to login
                </button>
              </p>
            </form>
          ) : (
            <>
              <div className="au-social-row">
                <button
                  className="au-social-btn"
                  onClick={() => handleGoogleLogin()}
                  disabled={socialLoading !== null}
                >
                  {socialLoading === 'google' ? (
                    <span className="au-social-spinner" />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  )}
                  Google
                </button>
                <button
                  className="au-social-btn"
                  onClick={handleMicrosoftLogin}
                  disabled={socialLoading !== null}
                >
                  {socialLoading === 'microsoft' ? (
                    <span className="au-social-spinner" />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
                  )}
                  Microsoft
                </button>
              </div>

              <div className="au-divider">
                <div className="au-divider-line" />
                <span className="au-divider-text">or continue with email</span>
                <div className="au-divider-line" />
              </div>

              {paymentSuccess && <div className="au-success">Payment successful! Please log in to access your account.</div>}
              {error && <div className="au-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="au-field">
                  <label className="au-label">Email Address</label>
                  <input className="au-input" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>

                <div className="au-field">
                  <div className="au-label-row">
                    <label className="au-label">Password</label>
                    <Link to="/forgot-password" className="au-forgot">Forgot password?</Link>
                  </div>
                  <div className="au-input-wrap">
                    <input className="au-input" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="au-eye-btn">
                      {showPassword ? <EyeOff size={18} color="var(--gray-500)" /> : <Eye size={18} color="var(--gray-500)" />}
                    </button>
                  </div>
                </div>

                <label className="au-remember">
                  <input type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
                  Remember me for 30 days
                </label>

                <button type="submit" className="au-submit-btn" disabled={loading}>
                  {loading ? 'Logging in…' : <><span>Log In</span><ArrowRight size={18} /></>}
                </button>
              </form>

              <p className="au-switch">
                Don't have an account? <Link to="/pricing">Sign up free</Link>
              </p>
            </>
          )}
        </div>
      </div>

      <style>{`
        .au-wrap { min-height: calc(100vh - 72px); display: flex; }

        .au-left {
          flex: 0 0 50%; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1A2D5A 0%, #243A6C 40%, #1c3562 100%);
          display: flex; flex-direction: column; justify-content: center; padding: 60px 56px;
        }
        .au-left-dots {
          position: absolute; inset: 0; opacity: 0.05;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .au-left-glow {
          position: absolute; bottom: -100px; right: -100px; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(77,184,232,0.15), transparent 70%);
          pointer-events: none;
        }
        .au-left-inner { position: relative; z-index: 1; }

        .au-logo-link { display: inline-block; margin-bottom: 48px; }
        .au-logo { height: 56px; }
        .au-left-title { font-size: 40px; font-weight: 700; color: #fff; line-height: 1.2; margin-bottom: 16px; }
        .au-left-desc { font-size: 18px; color: rgba(255,255,255,0.7); line-height: 1.6; margin-bottom: 48px; max-width: 440px; }

        .au-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .au-stat { display: flex; align-items: center; gap: 14px; }
        .au-stat-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: rgba(77,184,232,0.15);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .au-stat-value { font-size: 22px; font-weight: 700; color: #fff; }
        .au-stat-label { font-size: 13px; color: rgba(255,255,255,0.5); }

        .au-testimonial {
          margin-top: 56px; padding: 24px 28px;
          background: rgba(255,255,255,0.06); border-radius: 12px;
          border-left: 3px solid var(--sky);
        }
        .au-testimonial-quote { font-size: 15px; color: rgba(255,255,255,0.85); font-style: italic; line-height: 1.6; margin-bottom: 12px; }
        .au-testimonial-author { display: flex; align-items: center; gap: 10px; }
        .au-testimonial-avatar {
          width: 32px; height: 32px; border-radius: 50%; background: var(--sky);
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 12px; font-weight: 600;
        }
        .au-testimonial-name { font-size: 13px; font-weight: 600; color: #fff; }
        .au-testimonial-role { font-size: 12px; color: rgba(255,255,255,0.5); }

        .au-right {
          flex: 0 0 50%; display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #fff; position: relative;
        }
        .au-social-overlay {
          position: absolute; inset: 0; z-index: 10;
          background: rgba(255,255,255,0.97);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 16px;
        }
        .au-social-overlay-spinner {
          width: 48px; height: 48px; border-radius: 50%;
          border: 3px solid var(--gray-200); border-top-color: var(--sky);
          animation: au-spin 0.7s linear infinite;
        }
        .au-social-overlay-title { font-size: 18px; font-weight: 600; color: var(--navy); margin: 0; }
        .au-social-overlay-sub { font-size: 14px; color: var(--gray-500); margin: 0; }
        .au-right-inner { width: 100%; max-width: 440px; }
        .au-mobile-logo { display: none; text-align: center; margin-bottom: 24px; }
        .au-logo-sm { height: 44px; }

        .au-form-header { margin-bottom: 36px; }
        .au-form-title { font-size: 30px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .au-form-subtitle { font-size: 16px; color: var(--gray-500); }

        .au-social-row { display: flex; gap: 12px; margin-bottom: 24px; }
        .au-social-btn {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 12px 16px; border-radius: 10px; border: 1px solid var(--gray-200);
          background: #fff; font-size: 14px; font-weight: 500; color: var(--gray-900);
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .au-social-btn:hover:not(:disabled) { background: var(--gray-50); border-color: var(--gray-300); }
        .au-social-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .au-social-spinner {
          width: 16px; height: 16px; border-radius: 50%;
          border: 2px solid var(--gray-300); border-top-color: var(--sky);
          animation: au-spin 0.6s linear infinite; flex-shrink: 0;
        }
        @keyframes au-spin { to { transform: rotate(360deg); } }

        .au-divider { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
        .au-divider-line { flex: 1; height: 1px; background: var(--gray-200); }
        .au-divider-text { font-size: 13px; color: var(--gray-500); font-weight: 500; white-space: nowrap; }

        .au-error { background: #FFEBEE; color: var(--red); padding: 12px 16px; border-radius: 8px; font-size: 14px; margin-bottom: 20px; }

        .au-field { margin-bottom: 20px; }
        .au-label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-900); margin-bottom: 6px; }
        .au-label-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .au-forgot { font-size: 13px; font-weight: 500; color: var(--sky); text-decoration: none; }
        .au-forgot:hover { text-decoration: underline; }
        .au-input {
          width: 100%; height: 46px; padding: 0 14px; border-radius: 10px;
          border: 1px solid var(--gray-200); font-size: 15px; font-family: inherit;
          color: var(--gray-900); background: var(--gray-50); outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .au-input:focus { border-color: var(--sky); background: #fff; box-shadow: 0 0 0 3px rgba(77,184,232,0.1); }
        .au-input-wrap { position: relative; }
        .au-input-wrap .au-input { padding-right: 44px; }
        .au-eye-btn { position: absolute; right: 14px; top: 13px; background: none; border: none; padding: 0; cursor: pointer; }

        .au-remember {
          display: flex; align-items: center; gap: 8px; font-size: 14px;
          color: var(--gray-500); cursor: pointer; margin-bottom: 24px;
        }
        .au-remember input { width: 16px; height: 16px; accent-color: var(--sky); }

        .au-submit-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 48px; background: var(--green); color: #fff;
          border: none; border-radius: 10px; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: 0 4px 14px rgba(58,170,53,0.3); transition: all 0.2s;
          margin-bottom: 24px;
        }
        .au-submit-btn:hover:not(:disabled) { background: #2e9229; }
        .au-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .au-success { background: #E8F5E9; color: var(--green); padding: 12px 16px; border-radius: 8px; font-size: 14px; margin-bottom: 20px; }

        .au-switch { text-align: center; font-size: 15px; color: var(--gray-500); }
        .au-switch a { font-weight: 600; color: var(--sky); text-decoration: none; }
        .au-switch a:hover { text-decoration: underline; }

        @media (max-width: 900px) {
          .au-left { padding: 48px 36px; }
          .au-left-title { font-size: 32px; }
          .au-left-desc { font-size: 16px; margin-bottom: 32px; }
          .au-testimonial { margin-top: 36px; }
        }

        @media (max-width: 768px) {
          .au-wrap { flex-direction: column; }
          .au-left { flex: none; padding: 28px 24px; }
          .au-left-title { font-size: 24px; margin-bottom: 10px; }
          .au-left-desc { font-size: 14px; margin-bottom: 0; max-width: 100%; }
          .au-stats-grid { display: none; }
          .au-testimonial { display: none; }
          .au-logo-link { margin-bottom: 20px; }
          .au-logo { height: 40px; }
          .au-right { flex: none; padding: 28px 20px; }
          .au-form-title { font-size: 26px; }
          .au-form-subtitle { font-size: 14px; }
          .au-form-header { margin-bottom: 24px; }
        }

        @media (max-width: 480px) {
          .au-left { display: none; }
          .au-mobile-logo { display: block; margin-bottom: 16px; }
          .au-logo-sm { height: 40px; }
          .au-right { min-height: calc(100vh - 72px); padding: 32px 20px; align-items: flex-start; padding-top: 48px; }
          .au-right-inner { max-width: 100%; }
          .au-form-title { font-size: 24px; }
          .au-form-header { margin-bottom: 20px; }
          .au-social-row { flex-direction: column; gap: 10px; margin-bottom: 20px; }
          .au-social-btn { padding: 12px 16px; font-size: 15px; }
          .au-divider { margin-bottom: 20px; }
          .au-divider-text { font-size: 12px; }
          .au-input { height: 48px; font-size: 16px; }
          .au-submit-btn { height: 50px; font-size: 16px; }
          .au-field { margin-bottom: 16px; }
          .au-remember { margin-bottom: 20px; font-size: 14px; }
          .au-switch { font-size: 14px; }
        }

        @media (max-width: 360px) {
          .au-right { padding: 24px 16px; padding-top: 40px; }
          .au-form-title { font-size: 22px; }
          .au-label { font-size: 13px; }
          .au-forgot { font-size: 12px; }
        }
      `}</style>
    </div>
  );
}
